import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API, BACKEND_URL } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Plus, X, ChevronLeft, ChevronRight, Pencil, Trash2, Palette, ImageIcon } from 'lucide-react';
import ImageEditor from '@/components/ImageEditor';

function Cuadros() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cuadros, setCuadros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCuadro, setSelectedCuadro] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    año: '',
    tecnica: '',
    dimensiones: '',
    precio: '',
    disponible: true
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCuadros();
  }, []);

  const fetchCuadros = async () => {
    try {
      const response = await axios.get(`${API}/cuadros`);
      setCuadros(response.data);
    } catch (error) {
      console.error('Error fetching cuadros:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Abrir el editor de imagen
      const imageUrl = URL.createObjectURL(file);
      setTempImageSrc(imageUrl);
      setShowImageEditor(true);
    }
  };

  const handleEditorSave = (editedBlob) => {
    // Convertir el blob en un File
    const editedFile = new File([editedBlob], 'cuadro_editado.jpg', { type: 'image/jpeg' });
    setSelectedFile(editedFile);
    setPreview(URL.createObjectURL(editedBlob));
    setShowImageEditor(false);
    setTempImageSrc(null);
  };

  const handleEditorCancel = () => {
    setShowImageEditor(false);
    setTempImageSrc(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      alert('Por favor selecciona una imagen');
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', selectedFile);
      data.append('titulo', formData.titulo);
      if (formData.descripcion) data.append('descripcion', formData.descripcion);
      if (formData.año) data.append('año', formData.año);
      if (formData.tecnica) data.append('tecnica', formData.tecnica);
      if (formData.dimensiones) data.append('dimensiones', formData.dimensiones);
      if (formData.precio) data.append('precio', formData.precio);
      data.append('disponible', formData.disponible);

      await axios.post(`${API}/cuadros`, data, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      await fetchCuadros();
      resetForm();
      setShowAddDialog(false);
    } catch (error) {
      console.error('Error creating cuadro:', error);
      alert('Error al subir el cuadro');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedCuadro) return;

    try {
      await axios.put(`${API}/cuadros/${selectedCuadro.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchCuadros();
      setShowEditDialog(false);
      setSelectedCuadro(null);
    } catch (error) {
      console.error('Error updating cuadro:', error);
      alert('Error al actualizar');
    }
  };

  const handleDelete = async (cuadroId) => {
    if (!window.confirm('¿Eliminar este cuadro del portfolio?')) return;

    try {
      await axios.delete(`${API}/cuadros/${cuadroId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCuadros();
      setLightboxIndex(-1);
    } catch (error) {
      console.error('Error deleting cuadro:', error);
      alert('Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      descripcion: '',
      año: '',
      tecnica: '',
      dimensiones: '',
      precio: '',
      disponible: true
    });
    setSelectedFile(null);
    setPreview(null);
  };

  const openEdit = (cuadro) => {
    setSelectedCuadro(cuadro);
    setFormData({
      titulo: cuadro.titulo || '',
      descripcion: cuadro.descripcion || '',
      año: cuadro.año || '',
      tecnica: cuadro.tecnica || '',
      dimensiones: cuadro.dimensiones || '',
      precio: cuadro.precio || '',
      disponible: cuadro.disponible !== false
    });
    setShowEditDialog(true);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(-1);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev + 1) % cuadros.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev - 1 + cuadros.length) % cuadros.length);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === -1) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') setLightboxIndex((prev) => (prev + 1) % cuadros.length);
      if (e.key === 'ArrowLeft') setLightboxIndex((prev) => (prev - 1 + cuadros.length) % cuadros.length);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, cuadros.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-neutral-400">Cargando...</div>
      </div>
    );
  }

  const isAdmin = user?.role === 'entrenador';
  const currentCuadro = lightboxIndex >= 0 ? cuadros[lightboxIndex] : null;

  return (
    <div className="min-h-screen bg-neutral-950" data-testid="cuadros-page">
      {/* Header minimalista */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-neutral-950/90 backdrop-blur-sm border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-neutral-400 hover:text-white -ml-2"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Volver
          </Button>
          
          <h1 className="text-lg font-light tracking-widest text-neutral-200 flex items-center gap-2">
            <Palette className="h-5 w-5" />
            PORTFOLIO
          </h1>
          
          {isAdmin && (
            <Button
              onClick={() => setShowAddDialog(true)}
              className="bg-white text-black hover:bg-neutral-200"
            >
              <Plus className="h-4 w-4 mr-2" />
              Añadir
            </Button>
          )}
          {!isAdmin && <div className="w-24" />}
        </div>
      </header>

      {/* Grid de cuadros */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {cuadros.length === 0 ? (
            <div className="text-center py-20">
              <Palette className="h-16 w-16 mx-auto text-neutral-700 mb-4" />
              <p className="text-neutral-500">No hay cuadros en el portfolio</p>
              {isAdmin && (
                <Button
                  onClick={() => setShowAddDialog(true)}
                  className="mt-4 bg-white text-black hover:bg-neutral-200"
                >
                  Añadir primer cuadro
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {cuadros.map((cuadro, index) => (
                <div
                  key={cuadro.id}
                  className="group relative aspect-square bg-neutral-900 rounded-sm overflow-hidden cursor-pointer"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={`${BACKEND_URL}${cuadro.imagen}`}
                    alt={cuadro.titulo}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {/* Overlay con info */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-white font-medium text-sm">{cuadro.titulo}</h3>
                      {cuadro.año && (
                        <p className="text-neutral-400 text-xs mt-1">{cuadro.año}</p>
                      )}
                    </div>
                  </div>
                  {/* Badge de disponibilidad */}
                  {!cuadro.disponible && (
                    <div className="absolute top-3 right-3 bg-red-600 text-white text-xs px-2 py-1 rounded">
                      Vendido
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Lightbox */}
      {lightboxIndex >= 0 && currentCuadro && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Botón cerrar */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white/70 hover:text-white z-50"
          >
            <X className="h-8 w-8" />
          </button>

          {/* Navegación */}
          {cuadros.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-2 transition-colors"
              >
                <ChevronLeft className="h-10 w-10" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-2 transition-colors"
              >
                <ChevronRight className="h-10 w-10" />
              </button>
            </>
          )}

          {/* Imagen y detalles */}
          <div 
            className="flex flex-col lg:flex-row items-center gap-8 max-w-6xl mx-auto px-12"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Imagen */}
            <div className="flex-1 flex items-center justify-center">
              <img
                src={`${BACKEND_URL}${currentCuadro.imagen}`}
                alt={currentCuadro.titulo}
                className="max-h-[70vh] max-w-full object-contain rounded-sm shadow-2xl"
              />
            </div>

            {/* Detalles */}
            <div className="lg:w-80 text-white space-y-4">
              <h2 className="text-2xl font-light">{currentCuadro.titulo}</h2>
              
              {currentCuadro.año && (
                <p className="text-neutral-400 text-sm">{currentCuadro.año}</p>
              )}
              
              {currentCuadro.descripcion && (
                <p className="text-neutral-300 text-sm leading-relaxed">
                  {currentCuadro.descripcion}
                </p>
              )}
              
              <div className="space-y-2 pt-4 border-t border-neutral-800">
                {currentCuadro.tecnica && (
                  <p className="text-sm">
                    <span className="text-neutral-500">Técnica:</span>{' '}
                    <span className="text-neutral-300">{currentCuadro.tecnica}</span>
                  </p>
                )}
                {currentCuadro.dimensiones && (
                  <p className="text-sm">
                    <span className="text-neutral-500">Dimensiones:</span>{' '}
                    <span className="text-neutral-300">{currentCuadro.dimensiones}</span>
                  </p>
                )}
                {currentCuadro.precio && (
                  <p className="text-sm">
                    <span className="text-neutral-500">Precio:</span>{' '}
                    <span className="text-neutral-300">{currentCuadro.precio}</span>
                  </p>
                )}
              </div>

              {!currentCuadro.disponible && (
                <div className="bg-red-900/30 border border-red-800 rounded px-3 py-2 text-red-400 text-sm">
                  Este cuadro ya ha sido vendido
                </div>
              )}

              {/* Acciones admin */}
              {isAdmin && (
                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      closeLightbox();
                      openEdit(currentCuadro);
                    }}
                    className="flex-1 border-neutral-700 text-white hover:bg-neutral-800"
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(currentCuadro.id)}
                    className="border-red-800 text-red-400 hover:bg-red-900/30"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Contador */}
              <p className="text-neutral-600 text-xs text-center pt-4">
                {lightboxIndex + 1} / {cuadros.length}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Dialog añadir cuadro */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-neutral-900 border-neutral-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Añadir cuadro</DialogTitle>
            <DialogDescription className="text-neutral-400">
              Añade una nueva obra a tu portfolio
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Preview de imagen */}
            <div className="relative">
              <div 
                className="aspect-video bg-neutral-800 rounded-lg flex items-center justify-center cursor-pointer overflow-hidden border-2 border-dashed border-neutral-700 hover:border-neutral-600 transition-colors"
                onClick={() => document.getElementById('cuadro-file').click()}
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <Plus className="h-8 w-8 mx-auto text-neutral-600 mb-2" />
                    <p className="text-neutral-500 text-sm">Seleccionar imagen</p>
                    <p className="text-neutral-600 text-xs mt-1">Se abrirá el editor para recortar y ajustar</p>
                  </div>
                )}
              </div>
              {preview && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setTempImageSrc(preview);
                    setShowImageEditor(true);
                  }}
                  className="absolute bottom-2 right-2 bg-black/70 border-neutral-600 text-white hover:bg-black/90"
                >
                  <ImageIcon className="h-4 w-4 mr-1" />
                  Editar
                </Button>
              )}
            </div>
            <input
              id="cuadro-file"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />

            <div className="space-y-3">
              <div>
                <Label className="text-neutral-400">Título *</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              
              <div>
                <Label className="text-neutral-400">Descripción</Label>
                <Textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white resize-none"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-neutral-400">Año</Label>
                  <Input
                    value={formData.año}
                    onChange={(e) => setFormData({ ...formData, año: e.target.value })}
                    className="bg-neutral-800 border-neutral-700 text-white"
                    placeholder="2024"
                  />
                </div>
                <div>
                  <Label className="text-neutral-400">Técnica</Label>
                  <Input
                    value={formData.tecnica}
                    onChange={(e) => setFormData({ ...formData, tecnica: e.target.value })}
                    className="bg-neutral-800 border-neutral-700 text-white"
                    placeholder="Óleo sobre lienzo"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-neutral-400">Dimensiones</Label>
                  <Input
                    value={formData.dimensiones}
                    onChange={(e) => setFormData({ ...formData, dimensiones: e.target.value })}
                    className="bg-neutral-800 border-neutral-700 text-white"
                    placeholder="100x80 cm"
                  />
                </div>
                <div>
                  <Label className="text-neutral-400">Precio</Label>
                  <Input
                    value={formData.precio}
                    onChange={(e) => setFormData({ ...formData, precio: e.target.value })}
                    className="bg-neutral-800 border-neutral-700 text-white"
                    placeholder="500€"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="disponible"
                  checked={formData.disponible}
                  onChange={(e) => setFormData({ ...formData, disponible: e.target.checked })}
                  className="rounded border-neutral-700"
                />
                <Label htmlFor="disponible" className="text-neutral-400 cursor-pointer">
                  Disponible para venta
                </Label>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowAddDialog(false); resetForm(); }}
                className="flex-1 border-neutral-700 text-neutral-300 hover:bg-neutral-800"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={uploading || !selectedFile || !formData.titulo}
                className="flex-1 bg-white text-black hover:bg-neutral-200"
              >
                {uploading ? 'Subiendo...' : 'Añadir'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog editar cuadro */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-neutral-900 border-neutral-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Editar cuadro</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-3">
              <div>
                <Label className="text-neutral-400">Título *</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              
              <div>
                <Label className="text-neutral-400">Descripción</Label>
                <Textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white resize-none"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-neutral-400">Año</Label>
                  <Input
                    value={formData.año}
                    onChange={(e) => setFormData({ ...formData, año: e.target.value })}
                    className="bg-neutral-800 border-neutral-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-neutral-400">Técnica</Label>
                  <Input
                    value={formData.tecnica}
                    onChange={(e) => setFormData({ ...formData, tecnica: e.target.value })}
                    className="bg-neutral-800 border-neutral-700 text-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-neutral-400">Dimensiones</Label>
                  <Input
                    value={formData.dimensiones}
                    onChange={(e) => setFormData({ ...formData, dimensiones: e.target.value })}
                    className="bg-neutral-800 border-neutral-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-neutral-400">Precio</Label>
                  <Input
                    value={formData.precio}
                    onChange={(e) => setFormData({ ...formData, precio: e.target.value })}
                    className="bg-neutral-800 border-neutral-700 text-white"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="disponible-edit"
                  checked={formData.disponible}
                  onChange={(e) => setFormData({ ...formData, disponible: e.target.checked })}
                  className="rounded border-neutral-700"
                />
                <Label htmlFor="disponible-edit" className="text-neutral-400 cursor-pointer">
                  Disponible para venta
                </Label>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="flex-1 border-neutral-700 text-neutral-300 hover:bg-neutral-800"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-white text-black hover:bg-neutral-200"
              >
                Guardar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Editor de imagen */}
      {showImageEditor && tempImageSrc && (
        <ImageEditor
          imageSrc={tempImageSrc}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
        />
      )}
    </div>
  );
}

export default Cuadros;
